import React from 'react';
import { LegalLayout, LegalSection } from './LegalLayout';

const ulStyle: React.CSSProperties = { margin: '8px 0', paddingLeft: 22 };

export const PrivacyPolicy: React.FC = () => {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        VERA Health Technologies, LLC ("VERA," "we," "us") provides a software
        platform for Applied Behavior Analysis (ABA) therapy practices. This
        Privacy Policy explains how we collect, use, disclose, and safeguard
        information—including Protected Health Information (PHI)—when clinicians
        and staff use the VERA platform. We act as a Business Associate under the
        Health Insurance Portability and Accountability Act (HIPAA) and handle PHI
        on behalf of the covered entities (therapy practices) that use VERA.
      </p>

      <LegalSection heading="1. Information We Collect">
        <ul style={ulStyle}>
          <li><strong>Account information:</strong> name, email, role, credentials, badge number, and employer/practice.</li>
          <li><strong>Client (patient) records:</strong> demographics, diagnoses, treatment plans, assessments, and session data entered by clinical staff.</li>
          <li><strong>Session documentation:</strong> notes, dictation audio, verification photos, and GPS check-in coordinates used to confirm service delivery.</li>
          <li><strong>Usage and device data:</strong> log-in times, actions taken in the app, and technical data such as device type and app version.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Information">
        <ul style={ulStyle}>
          <li>To provide clinical documentation, scheduling, supervision, and reporting features.</li>
          <li>To verify session location and delivery for compliance and billing integrity.</li>
          <li>To generate AI-assisted note drafts and parent-friendly summaries at the direction of authorized clinicians.</li>
          <li>To secure the platform, prevent misuse, and meet legal and regulatory obligations.</li>
        </ul>
        <p>We do not sell personal information or PHI, and we do not use PHI for advertising.</p>
      </LegalSection>

      <LegalSection heading="3. How We Share Information">
        <p>
          We share information only as needed to operate the service and as
          permitted by our Business Associate Agreements:
        </p>
        <ul style={ulStyle}>
          <li>With the therapy practice (covered entity) that owns the record.</li>
          <li>With subprocessors (e.g., cloud hosting, secure AI processing) bound by confidentiality and HIPAA obligations.</li>
          <li>When required by law, subpoena, or to protect safety.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Data Retention">
        <p>
          Clinical records are retained for the period required by the covered
          entity and applicable state and federal law (commonly 6–10 years, and
          longer for minors). When a practice ends its use of VERA, records are
          returned or securely destroyed per the Business Associate Agreement.
          You may request deletion of your data as described on our{' '}
          <a href="/data-deletion" style={{ color: '#4F6367', fontWeight: 600 }}>Data Deletion</a> page.
        </p>
      </LegalSection>

      <LegalSection heading="5. Security Measures">
        <ul style={ulStyle}>
          <li>Encryption of data in transit (TLS) and at rest.</li>
          <li>Role-based access controls so users see only the data their role permits.</li>
          <li>Audit logging of access to sensitive records.</li>
          <li>Authentication safeguards and least-privilege infrastructure access.</li>
        </ul>
        <p>No system is perfectly secure, but we maintain administrative, technical, and physical safeguards consistent with the HIPAA Security Rule.</p>
      </LegalSection>

      <LegalSection heading="6. Your Rights">
        <p>
          Depending on your role and applicable law, you may have the right to
          access, correct, or request deletion of personal information, and to
          obtain a copy of PHI. Because VERA acts on behalf of therapy practices,
          patient requests are generally directed to the treating practice; we
          will assist that practice in fulfilling them. Contact us at{' '}
          <a href="mailto:support@veraaba.com" style={{ color: '#4F6367', fontWeight: 600 }}>support@veraaba.com</a>.
        </p>
      </LegalSection>

      <LegalSection heading="7. Children's Privacy (COPPA)">
        <p>
          VERA is a professional tool used by clinicians; it is not directed to
          children and children do not create accounts. Many ABA clients are
          minors, and their information is entered by authorized clinical staff as
          part of treatment. We handle any information relating to a child with
          the same HIPAA safeguards described above and only process it at the
          direction of the treating practice and the child's parent or guardian.
          We do not knowingly permit children to use the platform directly or
          collect information from children for any commercial purpose.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Policy from time to time. Material changes will be
          communicated through the platform or to the practices we serve. Continued
          use after an update constitutes acceptance of the revised Policy.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact Us">
        <p>
          Questions or privacy requests:{' '}
          <a href="mailto:support@veraaba.com" style={{ color: '#4F6367', fontWeight: 600 }}>support@veraaba.com</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
